import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { queryEvents, queryItemTypesWithLocations, queryStorageLocations, queryPackingOperations } from '../schema';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { usePackingListsByEvent } from '../hooks/usePackingListsByEvent';
import { getPackingListStats } from '../utils/packingListStats';
import {
    generatePackingTasks,
    generatePackingTasksFromLpSolution,
    ItemLocationData,
    locationKey,
    type LpDagNodeRow,
    type LpEdgeFlowRow,
} from '../utils/packingTaskGenerator';
import { inferRouteStructure } from '../utils/routeInference';
import type { SuggestedOperation } from '../utils/suggestPackingOperations';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { useNavigate } from 'react-router-dom';
import RouteVisualization from './RouteVisualization';
import { filterValidItemLocations } from '../utils/instantDbGuards';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreateOperation?: (sourceEventIds: string[], targetEventIds: string[]) => void;
    initialSourceEventIds?: string[];
    initialSuggestion?: SuggestedOperation;
    tripId?: string;
    /** Trip name when creating from trip detail (shown in "linked to trip" indicator) */
    tripName?: string;
    /** When set with tripId, tasks are generated from the trip LP solution (quantities from plan) */
    tripLpData?: { dagNodes: LpDagNodeRow[]; edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]> };
    /** When set (e.g. from trip detail), only these events are shown in source/target/also-pack-for options */
    allowedEventIds?: string[];
    /** When set (e.g. from trip detail), only these storage locations are shown in source/destination checkboxes */
    allowedStorageLocationIds?: string[];
    /** When set with tripId, used as fallback source for LP task generation when user selects no source */
    tripSourceStorageLocationId?: string;
    /** When set with tripId, used as fallback target for LP task generation when user selects no target storage */
    tripDestStorageLocationId?: string;
}

const TOUCH_TARGET_MIN_HEIGHT = 44;

export default function CreateOperationDialog({
    open,
    onClose,
    onCreateOperation: _onCreateOperation,
    initialSourceEventIds,
    initialSuggestion,
    tripId,
    tripName,
    tripLpData,
    allowedEventIds,
    allowedStorageLocationIds,
    tripSourceStorageLocationId,
    tripDestStorageLocationId,
}: Props) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data: eventsData, isLoading: eventsLoading } = queryEvents();
    const { data: storageLocationsData } = queryStorageLocations();
    const { data: itemTypesData } = queryItemTypesWithLocations();
    const { data: operationsData } = queryPackingOperations();
    const [selectedSourceEventIds, setSelectedSourceEventIds] = useState<Set<string>>(new Set(initialSourceEventIds || []));
    const [selectedTargetEventIds, setSelectedTargetEventIds] = useState<Set<string>>(new Set());
    const [selectedSourceStorageLocationIds, setSelectedSourceStorageLocationIds] = useState<Set<string>>(new Set());
    const [selectedTargetStorageLocationIds, setSelectedTargetStorageLocationIds] = useState<Set<string>>(new Set());
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    /** Synchronous guard against double-submit before React re-renders disabled state. */
    const creatingRef = useRef(false);
    const [adjustForCurrentInventory, setAdjustForCurrentInventory] = useState(true);

    // Update selections when dialog opens: initialSuggestion takes precedence over initialSourceEventIds
    useEffect(() => {
        if (!open) return;
        if (initialSuggestion) {
            setSelectedSourceEventIds(new Set(initialSuggestion.sourceEventIds));
            setSelectedTargetEventIds(new Set(initialSuggestion.targetEventIds));
            setSelectedSourceStorageLocationIds(new Set(initialSuggestion.sourceStorageLocationIds));
            setSelectedTargetStorageLocationIds(new Set(initialSuggestion.targetStorageLocationIds));
        } else if (initialSourceEventIds) {
            setSelectedSourceEventIds(new Set(initialSourceEventIds));
        }
    }, [open, initialSourceEventIds, initialSuggestion]);

    const events = useMemo(() => eventsData?.events || [], [eventsData]);
    const storageLocations = useMemo(() => {
        const list = [...(storageLocationsData?.storageLocations ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (allowedStorageLocationIds !== undefined) {
            const allowed = new Set(allowedStorageLocationIds);
            return list.filter(sl => allowed.has(sl.id));
        }
        return list;
    }, [storageLocationsData, allowedStorageLocationIds]);

    const { packingListItemsByEvent, eventsWithPackingLists } = usePackingListsByEvent(events, { allowedEventIds });

    // Events available as destination and also-pack-for. When allowedEventIds is set (trip context),
    // show all allowed events; otherwise restrict to active/future events.
    const targetEventOptions = useMemo(() => {
        if (allowedEventIds !== undefined) {
            return eventsWithPackingLists;
        }
        const now = new Date();
        return eventsWithPackingLists.filter(event => {
            if (!event.endDate) return true;
            return new Date(event.endDate as unknown as string) >= now;
        });
    }, [eventsWithPackingLists, allowedEventIds]);

    // Build itemLocationsByLocationKey: key = eventId or storage:storageLocationId
    const itemLocationsByLocationKey = useMemo(() => {
        const map = new Map<string, ItemLocationData[]>();
        if (!itemTypesData?.itemTypes) return map;

        const itemTypes = itemTypesData.itemTypes as Array<{
            id: string;
            itemLocations?: Array<{
                event?: { id: string } | null;
                storageLocation?: { id: string } | null;
                quantity?: number;
            }>;
        }>;

        for (const itemType of itemTypes) {
            const locs = filterValidItemLocations(itemType.itemLocations ?? []);
            for (const loc of locs) {
                const qty = loc.quantity ?? 0;
                if (qty <= 0) continue;
                const eventId = loc.event?.id ?? null;
                const storageLocationId = loc.storageLocation?.id ?? null;
                const data: ItemLocationData = {
                    itemTypeId: itemType.id,
                    eventId,
                    storageLocationId,
                    quantity: qty,
                };
                let key: string;
                if (eventId != null) {
                    key = eventId;
                } else if (storageLocationId != null) {
                    key = locationKey('storage', storageLocationId);
                } else {
                    continue;
                }
                const list = map.get(key) ?? [];
                list.push(data);
                map.set(key, list);
            }
        }
        return map;
    }, [itemTypesData]);

    const getInventoryCount = useCallback(
        (eventId: string) => {
            const locs = itemLocationsByLocationKey.get(eventId) ?? [];
            return locs.reduce((sum, loc) => sum + (loc.quantity ?? 0), 0);
        },
        [itemLocationsByLocationKey]
    );

    // Source events: packing list + current inventory (items actually at the event)
    const sourceEventOptions = useMemo(() => {
        return eventsWithPackingLists.filter(e => itemLocationsByLocationKey.has(e.id));
    }, [eventsWithPackingLists, itemLocationsByLocationKey]);

    const hasSource = selectedSourceStorageLocationIds.size > 0 || selectedSourceEventIds.size > 0;
    const hasTarget = selectedTargetStorageLocationIds.size > 0 || selectedTargetEventIds.size > 0;
    const isValid = useMemo(() => hasSource && hasTarget, [hasSource, hasTarget]);

    // Maps for route inference (event IDs and packing lists)
    const eventsMap = useMemo(() => {
        const map = new Map<string, { id: string; name?: string; startDate?: Date | null; endDate?: Date | null }>();
        for (const e of events) {
            map.set(e.id, {
                id: e.id,
                name: e.name,
                startDate: e.startDate ? new Date(e.startDate as unknown as string) : null,
                endDate: e.endDate ? new Date(e.endDate as unknown as string) : null,
            });
        }
        return map;
    }, [events]);

    const packingListsForInference = useMemo(() => {
        const map = new Map<string, Array<{ itemType?: { id: string; category?: { name: string } | null } | null }>>();
        packingListItemsByEvent.forEach((items, eventId) => {
            map.set(
                eventId,
                items.map(item => ({
                    itemType: item.itemType
                        ? { id: item.itemType.id, category: item.itemType.category ? { name: item.itemType.category.name } : null }
                        : null,
                }))
            );
        });
        return map;
    }, [packingListItemsByEvent]);

    const routeStructure = useMemo(() => {
        if (!hasSource && !hasTarget) return null;
        const sourceIds = Array.from(selectedSourceEventIds);
        const targetIds = Array.from(selectedTargetEventIds);
        const sourceStorageIds = Array.from(selectedSourceStorageLocationIds);
        const targetStorageIds = Array.from(selectedTargetStorageLocationIds);
        return inferRouteStructure(
            sourceIds,
            targetIds,
            eventsMap,
            packingListsForInference,
            sourceStorageIds as string[],
            targetStorageIds as string[]
        );
    }, [
        hasSource,
        hasTarget,
        selectedSourceEventIds,
        selectedTargetEventIds,
        selectedSourceStorageLocationIds,
        selectedTargetStorageLocationIds,
        eventsMap,
        packingListsForInference,
    ]);

    const getLocationName = useCallback(
        (locationKeyOrEventId: string): string => {
            if (locationKeyOrEventId.startsWith('storage:source:')) {
                const id = locationKeyOrEventId.slice(15);
                return storageLocations.find(s => s.id === id)?.name ?? id;
            }
            if (locationKeyOrEventId.startsWith('storage:dest:')) {
                const id = locationKeyOrEventId.slice(14);
                return storageLocations.find(s => s.id === id)?.name ?? id;
            }
            if (locationKeyOrEventId.startsWith('storage:')) {
                const id = locationKeyOrEventId.slice(8);
                return storageLocations.find(s => s.id === id)?.name ?? id;
            }
            return events.find(e => e.id === locationKeyOrEventId)?.name ?? locationKeyOrEventId;
        },
        [events, storageLocations]
    );

    const getTrailerName = useCallback(
        (trailerId: string) => itemTypesData?.itemTypes?.find((t: { id: string }) => t.id === trailerId)?.name ?? `Trailer ${trailerId}`,
        [itemTypesData]
    );

    // Validation errors from route inference (date conflicts)
    const routeValidationErrors = useMemo(() => {
        if (!routeStructure) return [];
        const errors: string[] = [];
        for (const c of routeStructure.conflicts) {
            const trailerName = getTrailerName(c.trailerId);
            errors.push(`Date conflict (${trailerName}): ${c.reason}`);
        }
        for (const w of routeStructure.warnings) {
            if (w.severity === 'error') errors.push(w.message);
        }
        return errors;
    }, [routeStructure, getTrailerName]);

    const hasRouteErrors = routeValidationErrors.length > 0;

    /** Whether LP data is available for this trip context */
    const isLpContext = Boolean(tripId && tripLpData && Object.keys(tripLpData.edgeFlowsByItemType).length > 0);

    /**
     * Preview tasks plus, when inventory adjustment is active, the unadjusted LP counts
     * so the UI can display how many tasks/items were reduced.
     */
    const previewTasksResult = useMemo(() => {
        const empty = {
            tasks: [] as ReturnType<typeof generatePackingTasks>,
            unadjustedCount: null as number | null,
            unadjustedQty: null as number | null,
        };
        if (!isValid || hasRouteErrors) return empty;
        const sourceEventIds = Array.from(selectedSourceEventIds);
        const targetEventIds = Array.from(selectedTargetEventIds);
        const sourceStorageLocationIds = Array.from(selectedSourceStorageLocationIds);
        const targetStorageLocationIds = Array.from(selectedTargetStorageLocationIds);
        const sourceEvents = events.filter(e => sourceEventIds.includes(e.id));
        const targetEvents = events.filter(e => targetEventIds.includes(e.id));
        const effectiveSourceStorageIds =
            sourceStorageLocationIds.length > 0
                ? sourceStorageLocationIds
                : tripSourceStorageLocationId
                  ? [tripSourceStorageLocationId]
                  : [];
        const effectiveTargetStorageIds =
            targetStorageLocationIds.length > 0 ? targetStorageLocationIds : tripDestStorageLocationId ? [tripDestStorageLocationId] : [];
        try {
            if (isLpContext && tripLpData) {
                // Always compute raw LP tasks (no adjustment) for comparison
                const rawTasks = generatePackingTasksFromLpSolution(
                    sourceEventIds,
                    targetEventIds,
                    effectiveSourceStorageIds,
                    effectiveTargetStorageIds,
                    tripLpData.dagNodes,
                    tripLpData.edgeFlowsByItemType,
                    packingListItemsByEvent
                );
                if (adjustForCurrentInventory) {
                    const adjustedTasks = generatePackingTasksFromLpSolution(
                        sourceEventIds,
                        targetEventIds,
                        effectiveSourceStorageIds,
                        effectiveTargetStorageIds,
                        tripLpData.dagNodes,
                        tripLpData.edgeFlowsByItemType,
                        packingListItemsByEvent,
                        itemLocationsByLocationKey
                    );
                    return {
                        tasks: adjustedTasks,
                        unadjustedCount: rawTasks.length,
                        unadjustedQty: rawTasks.reduce((s, t) => s + t.quantity, 0),
                    };
                }
                return { tasks: rawTasks, unadjustedCount: null, unadjustedQty: null };
            }
            return {
                tasks: generatePackingTasks(
                    {
                        sourceEventIds,
                        targetEventIds,
                        sourceStorageLocationIds,
                        targetStorageLocationIds,
                    },
                    sourceEvents,
                    targetEvents,
                    packingListItemsByEvent,
                    itemLocationsByLocationKey,
                    getLocationName
                ),
                unadjustedCount: null,
                unadjustedQty: null,
            };
        } catch {
            return empty;
        }
    }, [
        isValid,
        hasRouteErrors,
        isLpContext,
        adjustForCurrentInventory,
        selectedSourceEventIds,
        selectedTargetEventIds,
        selectedSourceStorageLocationIds,
        selectedTargetStorageLocationIds,
        tripLpData,
        tripSourceStorageLocationId,
        tripDestStorageLocationId,
        events,
        packingListItemsByEvent,
        itemLocationsByLocationKey,
        getLocationName,
    ]);

    const previewTasks = previewTasksResult.tasks;

    /** When adjustment is active, show how many tasks/items were reduced vs. raw LP plan. */
    const adjustmentSummary = useMemo(() => {
        if (!adjustForCurrentInventory || previewTasksResult.unadjustedCount === null || previewTasksResult.unadjustedQty === null)
            return null;
        const adjustedQty = previewTasks.reduce((s, t) => s + t.quantity, 0);
        const skippedTasks = previewTasksResult.unadjustedCount - previewTasks.length;
        const reducedQty = previewTasksResult.unadjustedQty - adjustedQty;
        if (skippedTasks <= 0 && reducedQty <= 0) return null;
        return { skippedTasks, reducedQty };
    }, [adjustForCurrentInventory, previewTasksResult, previewTasks]);

    /** Task count per source → destination pair for preview */
    const taskCountBySourceDest = useMemo(() => {
        const map = new Map<string, number>();
        for (const task of previewTasks) {
            const fromKey = task.fromEventId ?? (task.fromStorageLocationId ? locationKey('storage', task.fromStorageLocationId) : '');
            const toKey = task.toEventId ?? (task.toStorageLocationId ? locationKey('storage', task.toStorageLocationId) : '');
            if (!fromKey || !toKey) continue;
            const pair = `${fromKey}\t${toKey}`;
            map.set(pair, (map.get(pair) ?? 0) + 1);
        }
        const result: Array<{ fromKey: string; toKey: string; count: number }> = [];
        for (const [pair, count] of Array.from(map.entries())) {
            const [fromKey, toKey] = pair.split('\t');
            result.push({ fromKey, toKey, count });
        }
        return result;
    }, [previewTasks]);

    const handleCreate = async () => {
        if (!isValid) {
            setValidationError('Please select at least one source and one destination (HQ or events).');
            return;
        }
        if (hasRouteErrors) {
            setValidationError(routeValidationErrors[0] ?? 'Please fix route conflicts before creating.');
            return;
        }

        // Check for active operation
        const activeOperation = operationsData?.packingOperations?.find((op: any) => op.status === 'active');
        if (activeOperation) {
            setValidationError('An active operation already exists. Please complete it first.');
            return;
        }

        if (creatingRef.current) return;
        creatingRef.current = true;

        setIsCreating(true);
        setValidationError(null);

        try {
            const stillActive = operationsData?.packingOperations?.find((op: any) => op.status === 'active');
            if (stillActive) {
                setValidationError('An active operation already exists. Please complete it first.');
                return;
            }

            const sourceEventIds = Array.from(selectedSourceEventIds);
            const targetEventIds = Array.from(selectedTargetEventIds);
            const sourceStorageLocationIds = Array.from(selectedSourceStorageLocationIds);
            const targetStorageLocationIds = Array.from(selectedTargetStorageLocationIds);

            // Get source and target events
            const sourceEvents = events.filter(e => sourceEventIds.includes(e.id));
            const targetEvents = events.filter(e => targetEventIds.includes(e.id));

            // Generate tasks: from LP solution when trip-linked and plan available, else standalone.
            // When trip-linked, use trip source/dest as fallback for LP filtering so e.g. "targets only" still gets HQ→event flows.
            const effectiveSourceStorageIds =
                sourceStorageLocationIds.length > 0
                    ? sourceStorageLocationIds
                    : tripSourceStorageLocationId
                      ? [tripSourceStorageLocationId]
                      : [];
            const effectiveTargetStorageIds =
                targetStorageLocationIds.length > 0
                    ? targetStorageLocationIds
                    : tripDestStorageLocationId
                      ? [tripDestStorageLocationId]
                      : [];
            let tasks: ReturnType<typeof generatePackingTasks>;
            if (isLpContext && tripLpData) {
                tasks = generatePackingTasksFromLpSolution(
                    sourceEventIds,
                    targetEventIds,
                    effectiveSourceStorageIds,
                    effectiveTargetStorageIds,
                    tripLpData.dagNodes,
                    tripLpData.edgeFlowsByItemType,
                    packingListItemsByEvent,
                    adjustForCurrentInventory ? itemLocationsByLocationKey : undefined
                );
            } else {
                tasks = generatePackingTasks(
                    {
                        sourceEventIds,
                        targetEventIds,
                        sourceStorageLocationIds,
                        targetStorageLocationIds,
                    },
                    sourceEvents,
                    targetEvents,
                    packingListItemsByEvent,
                    itemLocationsByLocationKey,
                    getLocationName
                );
            }

            // Create operation and tasks in transaction
            const operationId = id();
            const now = new Date();

            const txs: Parameters<typeof db.transact>[0] = [
                db.tx.packingOperations[operationId].update({
                    createdAt: now,
                    status: 'active',
                    completedAt: null,
                }),
            ];

            if (sourceEventIds.length > 0) {
                txs.push(db.tx.packingOperations[operationId].link({ sourceEvents: sourceEventIds }));
            }
            if (targetEventIds.length > 0) {
                txs.push(db.tx.packingOperations[operationId].link({ targetEvents: targetEventIds }));
            }
            if (sourceStorageLocationIds.length > 0) {
                txs.push(db.tx.packingOperations[operationId].link({ sourceStorageLocations: sourceStorageLocationIds }));
            }
            if (targetStorageLocationIds.length > 0) {
                txs.push(db.tx.packingOperations[operationId].link({ targetStorageLocations: targetStorageLocationIds }));
            }

            // Create tasks
            for (const task of tasks) {
                const taskId = id();
                txs.push(
                    db.tx.packingTasks[taskId].update({
                        quantity: task.quantity,
                        completed: false,
                        completedAt: null,
                        ambiguous: task.ambiguous,
                    })
                );
                txs.push(db.tx.packingTasks[taskId].link({ packingOperation: operationId }));
                txs.push(db.tx.packingTasks[taskId].link({ itemType: task.itemTypeId }));
                if (task.fromEventId) {
                    txs.push(db.tx.packingTasks[taskId].link({ fromEvent: task.fromEventId }));
                }
                if (task.fromStorageLocationId) {
                    txs.push(db.tx.packingTasks[taskId].link({ fromStorageLocation: task.fromStorageLocationId }));
                }
                if (task.toEventId) {
                    txs.push(db.tx.packingTasks[taskId].link({ toEvent: task.toEventId }));
                }
                if (task.toStorageLocationId) {
                    txs.push(db.tx.packingTasks[taskId].link({ toStorageLocation: task.toStorageLocationId }));
                }
            }

            if (tripId) {
                txs.push(db.tx.trips[tripId].link({ packingOperations: [operationId] }));
            }

            await db.transact(txs);

            // Close dialog and navigate
            handleClose();
            navigate(`/logistics/operation/${operationId}/tasks`);
        } catch (error: any) {
            console.error('Failed to create operation:', error);
            setValidationError(error.message || 'Failed to create operation. Please try again.');
        } finally {
            creatingRef.current = false;
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        setSelectedSourceEventIds(new Set(initialSourceEventIds || []));
        setSelectedTargetEventIds(new Set());
        setSelectedSourceStorageLocationIds(new Set());
        setSelectedTargetStorageLocationIds(new Set());
        setValidationError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={isCreating ? undefined : handleClose} maxWidth="md" fullWidth fullScreen={false}>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Create Packing Operation</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    {/* Linked to trip indicator */}
                    {tripId && (
                        <Alert severity="info" icon={false} sx={{ fontWeight: 500 }}>
                            This operation will be linked to the trip{tripName ? `: ${tripName}` : ''}.
                        </Alert>
                    )}

                    {/* Inventory adjustment toggle — only shown for trip+LP operations */}
                    {isLpContext && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={adjustForCurrentInventory}
                                    onChange={e => setAdjustForCurrentInventory(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Adjust tasks for current destination inventory
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                        (recommended when resuming a partially-completed step)
                                    </Typography>
                                </Typography>
                            }
                        />
                    )}
                    {/* Validation Error */}
                    {validationError && (
                        <Alert severity="error" onClose={() => setValidationError(null)}>
                            {validationError}
                        </Alert>
                    )}
                    {/* Route validation errors (date conflicts, route validation) */}
                    {hasRouteErrors && routeValidationErrors.length > 0 && (
                        <Alert severity="error">
                            {routeValidationErrors.length === 1 ? (
                                routeValidationErrors[0]
                            ) : (
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {routeValidationErrors.map((msg, i) => (
                                        <li key={i}>{msg}</li>
                                    ))}
                                </ul>
                            )}
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Fix conflicts (e.g. deselect overlapping events) then try again.
                            </Typography>
                        </Alert>
                    )}

                    {/* Source Selection */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Source locations
                        </Typography>
                        {storageLocations.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {storageLocations.map(sl => (
                                    <FormControlLabel
                                        key={sl.id}
                                        control={
                                            <Checkbox
                                                checked={selectedSourceStorageLocationIds.has(sl.id)}
                                                onChange={e => {
                                                    setSelectedSourceStorageLocationIds((prev: Set<string>) => {
                                                        const next = new Set(prev);
                                                        if (e.target.checked) next.add(sl.id);
                                                        else next.delete(sl.id);
                                                        return next;
                                                    });
                                                    setValidationError(null);
                                                }}
                                                sx={isMobile ? { padding: 1 } : undefined}
                                            />
                                        }
                                        label={sl.name}
                                        sx={isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT, justifyContent: 'center' } : {}}
                                    />
                                ))}
                            </Box>
                        )}
                        {eventsLoading ? (
                            <CircularProgress size={24} />
                        ) : sourceEventOptions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No events with current inventory
                            </Typography>
                        ) : (
                            <Autocomplete
                                multiple
                                options={sourceEventOptions}
                                getOptionLabel={option => (option as { name?: string | null }).name ?? ''}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={events.filter(e => selectedSourceEventIds.has(e.id))}
                                onChange={(_e, newValue) => {
                                    setSelectedSourceEventIds(new Set(newValue.map(e => e.id)));
                                    setValidationError(null);
                                }}
                                renderInput={params => <TextField {...params} placeholder="Select source events" size="small" />}
                                renderOption={(props, event) => {
                                    const itemCount = getInventoryCount(event.id);
                                    const { trailerNames } = getPackingListStats(packingListItemsByEvent, event.id);
                                    const dateStr = event.startDate
                                        ? new Date(event.startDate as unknown as string).toLocaleDateString()
                                        : 'No date';
                                    return (
                                        <li {...props} key={event.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}>
                                                <Typography variant="body1">{event.name ?? ''}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {dateStr}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        has {itemCount} item{itemCount !== 1 ? 's' : ''}
                                                    </Typography>
                                                    {trailerNames.length > 0 && (
                                                        <>
                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                •
                                                            </Typography>
                                                            {trailerNames.map(name => (
                                                                <Chip key={name} label={name} size="small" variant="outlined" />
                                                            ))}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </li>
                                    );
                                }}
                            />
                        )}
                    </Box>

                    {/* Target Selection */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Destination locations
                        </Typography>
                        {storageLocations.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {storageLocations.map(sl => (
                                    <FormControlLabel
                                        key={sl.id}
                                        control={
                                            <Checkbox
                                                checked={selectedTargetStorageLocationIds.has(sl.id)}
                                                onChange={e => {
                                                    setSelectedTargetStorageLocationIds((prev: Set<string>) => {
                                                        const next = new Set(prev);
                                                        if (e.target.checked) next.add(sl.id);
                                                        else next.delete(sl.id);
                                                        return next;
                                                    });
                                                    setValidationError(null);
                                                }}
                                                sx={isMobile ? { padding: 1 } : undefined}
                                            />
                                        }
                                        label={sl.name}
                                        sx={isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT, justifyContent: 'center' } : {}}
                                    />
                                ))}
                            </Box>
                        )}
                        {eventsLoading ? (
                            <CircularProgress size={24} />
                        ) : targetEventOptions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No active or future events with packing lists
                            </Typography>
                        ) : (
                            <Autocomplete
                                multiple
                                options={targetEventOptions}
                                getOptionLabel={option => (option as { name?: string | null }).name ?? ''}
                                value={events.filter(e => selectedTargetEventIds.has(e.id))}
                                onChange={(_e, newValue) => {
                                    setSelectedTargetEventIds(new Set(newValue.map(e => e.id)));
                                    setValidationError(null);
                                }}
                                renderInput={params => <TextField {...params} placeholder="Select destination events" size="small" />}
                                renderOption={(props, event) => {
                                    const { itemCount, trailerNames } = getPackingListStats(packingListItemsByEvent, event.id);
                                    const dateStr = event.startDate
                                        ? new Date(event.startDate as unknown as string).toLocaleDateString()
                                        : 'No date';
                                    return (
                                        <li {...props} key={event.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}>
                                                <Typography variant="body1">{event.name ?? ''}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {dateStr}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        needs {itemCount} item{itemCount !== 1 ? 's' : ''}
                                                    </Typography>
                                                    {trailerNames.length > 0 && (
                                                        <>
                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                •
                                                            </Typography>
                                                            {trailerNames.map(name => (
                                                                <Chip key={name} label={name} size="small" variant="outlined" />
                                                            ))}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </li>
                                    );
                                }}
                            />
                        )}
                    </Box>

                    {/* Route Analysis */}
                    {isValid && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Route analysis
                            </Typography>
                            <RouteVisualization
                                routeStructure={routeStructure}
                                getNodeLabel={getLocationName}
                                getTrailerName={getTrailerName}
                            />
                            {routeStructure &&
                                routeStructure.conflicts.length === 0 &&
                                routeStructure.warnings.filter(w => w.severity === 'error').length === 0 && (
                                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                        No conflicts detected
                                    </Typography>
                                )}
                        </Box>
                    )}

                    {/* Task count by source → destination */}
                    {isValid && taskCountBySourceDest.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Tasks to create
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {taskCountBySourceDest.map(({ fromKey, toKey, count }) => (
                                    <Typography key={`${fromKey}-${toKey}`} component="li" variant="body2">
                                        {getLocationName(fromKey)} → {getLocationName(toKey)}: {count} task{count !== 1 ? 's' : ''}
                                    </Typography>
                                ))}
                            </Box>
                            {adjustmentSummary && (
                                <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
                                    <Typography variant="body2">
                                        <strong>Adjusted for current inventory:</strong>{' '}
                                        {adjustmentSummary.skippedTasks > 0 && (
                                            <>
                                                {adjustmentSummary.skippedTasks} task{adjustmentSummary.skippedTasks !== 1 ? 's' : ''}{' '}
                                                skipped{adjustmentSummary.reducedQty > 0 ? ', ' : ''}
                                            </>
                                        )}
                                        {adjustmentSummary.reducedQty > 0 && (
                                            <>
                                                {adjustmentSummary.reducedQty} fewer item{adjustmentSummary.reducedQty !== 1 ? 's' : ''} to
                                                move
                                            </>
                                        )}{' '}
                                        — destinations already have some items from previous work.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                    {/* No tasks to create after adjustment */}
                    {isValid && !hasRouteErrors && isLpContext && taskCountBySourceDest.length === 0 && adjustForCurrentInventory && (
                        <Alert severity="success" icon={false}>
                            <Typography variant="body2">
                                All destinations already have the required inventory — no tasks needed for this step.
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={isMobile ? { '& .MuiButton-root': { minHeight: TOUCH_TARGET_MIN_HEIGHT } } : undefined}>
                <Button onClick={handleClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button onClick={handleCreate} variant="contained" disabled={!isValid || hasRouteErrors || isCreating}>
                    {isCreating ? 'Creating...' : 'Create Operation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
